import {Flex, message, Space, Typography} from 'antd';
import {useTranslation} from '../../i18n/i18n';
import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import './TenantPage.scss';
import {AuditOutlined} from '@ant-design/icons';
import TenantsTable, {TenantRow} from '../../components/tenants/TenantsTable';
import {tenantApi} from '../../api/tenant.api';
import CreateTenantModal from '../../components/tenants/CreateTenantModal';
import useTenantStore, {ALL_TENANTS_VALUE} from '../../stores/tenant.store';
import {userApi} from '../../api/user.api';
import {dashboardPath, locationInstallationPath} from '../../routes';
import {ADAPTIVE_TABLE_CONTAINER_CLASS, useAdaptivePageSize} from '../../hooks/useAdaptivePageSize';

const FIRST_PAGE = 1;
const { Title } = Typography;

const TenantPage = () => {
  const { t } = useTranslation('tenants');
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [listTenants, setListTenants] = useState<TenantRow[]>([]);
  const [totalTenants, setTotalTenants] = useState(0);
  const [page, setPage] = useState(FIRST_PAGE);
  const refreshSidebarTenants = useTenantStore((state) => state.fetchTenants);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const [loading, setLoading] = useState(false);
  const { pageSize, containerRef, containerHeight, recalculate } = useAdaptivePageSize();
  const fetchTenantsRequestId = useRef(0);

  useEffect(() => {
    if (selectedTenant !== ALL_TENANTS_VALUE) {
      navigate(`/${dashboardPath}/${locationInstallationPath}`);
    }
  }, [selectedTenant, navigate]);

  const fetchTenants = useCallback(async () => {
    const requestId = ++fetchTenantsRequestId.current;
    setLoading(true);
    try {
      const response = await tenantApi.listTenantsByPage(page, pageSize);
      const admins = await Promise.all(
        response.tenants.map((tenant) => userApi.getAdminByTenant(tenant.uuid).catch(() => [])),
      );
      if (requestId !== fetchTenantsRequestId.current) return;
      const rows: TenantRow[] = response.tenants.map((tenant, i) => ({
        ...tenant,
        adminNames: admins[i].length > 0 ? admins[i].map((admin) => admin.name).join(', ') : '-',
      }));
      setListTenants(rows);
      setTotalTenants(response.metadata?.totalCount ?? 0);
      refreshSidebarTenants();
    } catch (error) {
      if (requestId === fetchTenantsRequestId.current) {
        messageApi.error(t('common:errors.generic'));
      }
    } finally {
      if (requestId === fetchTenantsRequestId.current) {
        setLoading(false);
      }
    }
  }, [t, messageApi, refreshSidebarTenants, page, pageSize]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    setPage(FIRST_PAGE);
  }, [pageSize]);

  useLayoutEffect(() => {
    recalculate();
  }, [totalTenants, recalculate]);

  return (
    <Flex vertical className={'TenantPage'}>
      {contextHolder}
      <Space size={'middle'} className={'TenantPage__header'}>
        <div className={'TenantPage__header__titleArea'}>
          <AuditOutlined className={'TenantPage__icon'} />
          <Title level={1} className={'Title'}>
            {t('title')}
          </Title>
        </div>

        <CreateTenantModal onSuccess={fetchTenants} />
      </Space>
      <div
        className={`TenantPage__table ${ADAPTIVE_TABLE_CONTAINER_CLASS}`}
        style={{ height: containerHeight }}
        ref={containerRef}
      >
        <TenantsTable
          tenants={listTenants}
          onSuccess={fetchTenants}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total: totalTenants,
            showSizeChanger: false,
          }}
          onChange={(pagination) => setPage(pagination.current ?? FIRST_PAGE)}
        />
      </div>
    </Flex>
  );
};

export default TenantPage;
